import React, { useRef } from 'react';
import styled            from 'styled-components';
import {
  DatePicker,
  DayOfWeek,
  DefaultButton,
  IDatePickerStrings,
  MaskedTextField,
} from 'office-ui-fabric-react';
import moment from 'moment';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Modal               from 'views/components/UI/DefaultModal';
import PrimaryButton       from 'views/components/UI/PrimaryButton';

export type DateType = 'startDate' | 'endDate';

const ModalBody = styled.div<{ withoutTime?: boolean }>`
  display: flex;
  justify-content: ${({ withoutTime }) => withoutTime ? 'center' : 'space-between'};
  flex-wrap: wrap;
  width: ${({ withoutTime }) => withoutTime ? 270 : 374}px;
  padding: 24px 27px 0;
  background: #ffffff;
`;

const StyledTextField = styled.div`
  width: 156px;

  span {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
  }
`;

const StyledFooter = styled.div<{ withoutTime?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 36px 0 21px;
  background: #ffffff;

  button {
    width: ${({ withoutTime }) => withoutTime ? 100 : 156}px;
  }
`;

const stringToDate = (string?: string) =>
  string ? moment(string, 'DD.MM.YYYY').toDate() : undefined;

const formatDate = (date?: Date | null) =>
  date ? moment(date).format('DD.MM.YYYY') : '';

const formatSubmitValue = (date: string, time: string) =>
  moment(`${date} ${time}`, 'DD.MM.YYYY HH:mm').toDate();

const formatSubmitValueWithoutTime = (date: string) =>
  moment(`${date}`, 'DD.MM.YYYY').toDate();

const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

const ValidationSchema = Yup.object().shape({
  date: Yup.string().required('Выберите дату'),
  time: Yup.string()
    .required('Укажите время')
    .matches(timeRegex, 'Введите значение от 00:00 до 23:59'),
});

interface IProps {
  onSelectDate: (value: Date) => void;
  onCloseModal: () => void;
  onCancel?: () => void;
  selectedValue?: Date;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  withoutTime?: boolean;
  selectedField?: string;
  periodStart?: Date | undefined;
  periodEnd?: Date | undefined;
  isDisabledDate?: boolean;
}

const ModalDatePicker: React.FC<IProps> = ({
  onSelectDate,
  onCloseModal,
  selectedValue,
  onCancel,
  minDate,
  maxDate,
  withoutTime = false,
  selectedField,
  isDisabledDate,
  periodStart,
  periodEnd
}) => {
  const timeInputRef = useRef<MaskedTextField>(null);

  return (
    <Modal handleClose={onCloseModal} title={'Выберите дату'}>
      <Formik
        initialValues={{
          date: moment(selectedValue ? selectedValue : new Date())
                  .format(
                    'DD.MM.YYYY',
                  ),
          time: moment(selectedValue ? selectedValue : new Date())
                  .format(
                    'HH:mm',
                  ),
        }}
        validationSchema={ValidationSchema}
        onSubmit={(values) => {
          onSelectDate(withoutTime
                       ? formatSubmitValueWithoutTime(values.date)
                       : formatSubmitValue(values.date, values.time));
          onCloseModal();
        }}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({
            values,
            setFieldValue,
            handleBlur,
            setFieldTouched,
            validateForm,
            handleChange,
            errors,
            touched,
            handleSubmit,
          }) => (
          <ModalBody withoutTime={withoutTime}>
            <StyledTextField>
              <DatePicker
                label={'Выберите дату'}
                firstDayOfWeek={DayOfWeek.Monday}
                strings={DayPickerStrings}
                onSelectDate={(date?: Date | null) =>
                  setFieldValue('date', formatDate(date))
                }
                value={stringToDate(values.date)}
                onBlur={() => setFieldTouched('date')}
                placeholder={formatDate(new Date())}
                formatDate={formatDate}
                aria-required={false}
                minDate={isDisabledDate ? (selectedField === 'startDate' ? minDate : periodStart) : minDate}
                maxDate={isDisabledDate ? (selectedField === 'endDate' ? maxDate : periodEnd) : maxDate}
                isRequired
              />
            </StyledTextField>
            {
              !withoutTime &&
              <StyledTextField>
                <MaskedTextField
                  ref={timeInputRef}
                  label={'Введите время'}
                  name={'time'}
                  value={values.time}
                  onChange={(_ev, newValue) => setFieldValue('time', newValue)}
                  onBlur={(ev) => handleBlur(ev)}
                  placeholder={'06:00'}
                  mask={'99:99'}
                  maskChar={''}
                  errorMessage={touched.time ? errors.time : ''}
                  styles={{
                    field: {
                      color: touched.time && errors.time ? 'red' : 'inherit',
                    },
                  }}
                />
              </StyledTextField>
            }
            <StyledFooter withoutTime={withoutTime}>
              <PrimaryButton
                onClick={() => {
                  validateForm()
                    .then((res) => {
                      if (res.time || res.date) {
                        if (res.time) {
                          timeInputRef.current?.focus();
                        }
                      } else {
                        handleSubmit();
                      }
                    })
                }}
              >
                Выбрать
              </PrimaryButton>
              <DefaultButton
                onClick={() => {
                  onCancel && onCancel();
                  onCloseModal();
                }}
              >
                Сбросить
              </DefaultButton>
            </StyledFooter>
          </ModalBody>
        )}
      </Formik>
    </Modal>
  );
};

const DayPickerStrings: IDatePickerStrings = {
  months: [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
  ],

  shortMonths: [
    'Янв',
    'Фев',
    'Мар',
    'Апр',
    'Май',
    'Июн',
    'Июл',
    'Авг',
    'Сен',
    'Окт',
    'Ноя',
    'Дек',
  ],

  days: [
    'Воскресенье',
    'Понеделик',
    'Вторник',
    'Среда',
    'Четверг',
    'Пятница',
    'Суббота',
  ],

  shortDays: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],

  isRequiredErrorMessage: 'Выберите дату',

  goToToday: 'Перейти к сегодня',
  prevMonthAriaLabel: 'Предыдущий месяц',
  nextMonthAriaLabel: 'Следующий месяц',
  prevYearAriaLabel: 'Следующий год',
  nextYearAriaLabel: 'Предыдущий год',
  closeButtonAriaLabel: 'Закрыть выбор даты',
};

export default ModalDatePicker;
